/* Product gallery arrows.

   The CSS turns the desktop media column into a horizontal scroll-snap track;
   this adds what CSS cannot: arrows that step by exactly one frame, disable
   themselves at each end, and a position counter. Dragging, trackpad and
   keyboard scrolling are the browser's own behaviour.

   Desktop only, and only where there is more than one frame to move between. */

(() => {
  const DESKTOP = '(min-width: 960px)';

  /* Observers are held here for the life of the page. A MutationObserver is
     only weakly referenced by the node it watches, so
     `new MutationObserver(cb).observe(node)` with nothing retaining it is
     eligible for garbage collection — it fires reliably for a few seconds
     after load and then silently stops. That is exactly how this failed: a
     wipe immediately after load rebuilt the controls, a colour change a few
     seconds later did nothing at all. */
  const observers = [];

  const ICON = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M1.875 7.438 12 17.563 22.125 7.438" stroke="currentColor" stroke-width="2"/></svg>';

  const build = (container) => {
    if (container.dataset.mettaGallery) return;

    const track = container.querySelector('.product__media');
    const frames = container.querySelectorAll('.product__media-item');
    if (!track || frames.length < 2) return;

    container.dataset.mettaGallery = 'true';

    const button = (direction, label) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `metta-gallery-nav metta-gallery-nav--${direction === -1 ? 'prev' : 'next'}`;
      el.setAttribute('aria-label', label);
      el.innerHTML = ICON;
      el.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth * direction });
      });
      return el;
    };

    const prev = button(-1, 'Fyrri mynd');
    const next = button(1, 'Næsta mynd');

    /* The same dots the mobile carousel uses, so one product does not have two
       ways of saying where you are. Mobile's are Swiper's own; these are built
       to match its markup and behaviour — clickable, one per frame. */
    const dots = document.createElement('div');
    dots.className = 'metta-gallery-dots';

    const bullets = [...frames].map((frame, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'metta-gallery-dot';
      dot.setAttribute('aria-label', `Mynd ${index + 1} af ${frames.length}`);
      dot.addEventListener('click', () => {
        track.scrollTo({ left: track.clientWidth * index });
      });
      dots.append(dot);
      return dot;
    });

    container.append(prev, next, dots);

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      const x = track.scrollLeft;

      // A pixel of slack: scrollLeft is fractional under zoom and on trackpads,
      // so an exact comparison leaves an arrow live with nowhere to go.
      prev.disabled = x <= 1;
      next.disabled = x >= max - 1;

      const index = Math.min(Math.round(x / track.clientWidth), frames.length - 1);
      bullets.forEach((dot, i) => {
        dot.classList.toggle('is-current', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    };

    let frame;
    track.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        sync();
      });
    }, { passive: true });

    const resize = new ResizeObserver(() => sync());
    resize.observe(track);
    observers.push(resize);

    sync();
  };

  /* The sibling colour swap rebuilds the media container with
     `oldMedia.innerHTML = newMedia.innerHTML`, which throws away the arrows and
     dots appended here — and it fires no event, so nothing asked us to put them
     back. Watching the container's own children covers that, and every other
     path that re-renders the gallery, without needing to know about any of
     them.

     Kept in a WeakSet rather than a data attribute because the same swap runs
     _syncElementAttrs, which strips attributes the incoming element does not
     have — a marker stored on the element would be wiped along with them. */
  const watched = new WeakSet();


  const rebuild = (container) => {
    // Our own append() mutates childList too; this is what stops the loop.
    if (container.querySelector('.metta-gallery-nav')) return;
    delete container.dataset.mettaGallery;
    build(container);
  };

  const watch = (container) => {
    if (watched.has(container)) return;
    watched.add(container);
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        if (window.matchMedia(DESKTOP).matches) rebuild(container);
      });
    });
    observer.observe(container, { childList: true });
    observers.push(observer);
  };

  /* There are two ways the gallery gets re-rendered, and watching the media
     container only covers one of them:

       _applySiblingProductHTML  replaces the container's innerHTML — the
                                 container element survives, so its own
                                 observer fires.
       _refreshOverviewWithVariant  replaces .product__primary-left's innerHTML
                                 — the container element is destroyed and a new
                                 one built, taking its observer with it. That
                                 path runs whenever enable_multiple_variant_media
                                 is on and the variant's featured image differs.

     So the stable ancestor is watched too. It is never itself replaced, only
     its children, and childList (not subtree) means our own appends inside the
     container cannot retrigger it. */
  const watchAncestor = (ancestor) => {
    if (watched.has(ancestor)) return;
    watched.add(ancestor);
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        run();
      });
    });
    observer.observe(ancestor, { childList: true });
    observers.push(observer);
  };

  const run = (root = document) => {
    document.querySelectorAll('.product__primary-left').forEach(watchAncestor);

    root.querySelectorAll('.product__media-container.above-mobile').forEach((container) => {
      // Watch regardless of viewport, so a container that was first seen on a
      // phone still gets its controls if the window is widened later.
      watch(container);
      if (window.matchMedia(DESKTOP).matches) build(container);
    });
  };

  run();
  window.addEventListener('load', () => run());
  window.matchMedia(DESKTOP).addEventListener('change', () => run());

  // Theme editor re-renders a section from scratch.
  document.addEventListener('shopify:section:load', (event) => run(event.target));
})();
